import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ChevronLeft, Edit2 } from 'react-feather';

import config from '../../../../config';
import TenantProfileStrip from '../shared/TenantProfileStrip';
import TenantStatusModal from '../shared/TenantStatusModal';

import '../../../ControlPanel/sadmin.css';
import '../../Settings/settings.css';
import '../shared/tenant-form.css';

const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'tenancy', label: 'Tenancy & charges' },
    { id: 'activity', label: 'Activity' },
];

const Kv = ({ label, value, full = false }) => (
    <div className="v3-sadmin-kv-item" style={full ? { gridColumn: '1 / -1' } : undefined}>
        <span className="v3-sadmin-kv-label">{label}</span>
        <span className="v3-sadmin-kv-value">{value || '—'}</span>
    </div>
);

const fmtDate = (d) => {
    if (!d) return '';
    try {
        const date = new Date(d);
        if (Number.isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
    } catch {
        return '';
    }
};

/**
 * /admin/tenant/tenantdetail/:id — Tenant detail, redesigned with tabs.
 *
 * Replaces the 30-field vertical scroll with three focused tabs:
 *   - Overview        — brand, identity, address, store details, contact
 *   - Tenancy & charges — lease dates, rent, revenue share, dynamic charges, delivery partners
 *   - Activity        — created/modified info + status history
 *
 * Tab state lives in the URL (`?tab=...`) so refresh + share preserves
 * the view. Edit + Lease + Change-status actions live in the hero
 * (status uses the inline TenantStatusModal — no full page nav).
 */
const TenantDtl = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const manageTenants = localStorage.getItem('manageTenants');
    const manageGeneralSettings = localStorage.getItem('manageGeneralSettings');
    const accessTenants = localStorage.getItem('accessTenants');
    const accessGeneralSettings = localStorage.getItem('accessGeneralSettings');
    const manageLeaseDetails = localStorage.getItem('manageLeaseDetails');
    const concessionaireId = localStorage.getItem('concessionaireId');
    const canManage = manageTenants === '1' || manageGeneralSettings === '1';
    const canEditLease = manageLeaseDetails === '1';
    const canView = canManage || accessTenants === '1' || accessGeneralSettings === '1';

    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [tenant, setTenant] = useState(null);
    const [deliveryPartners, setDeliveryPartners] = useState([]);
    const [charges, setCharges] = useState([]);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const tab = searchParams.get('tab') || 'overview';
    const setTab = useCallback((t) => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', t);
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (!canView) {
            window.alert(t('You are not authorized to view this page'));
            window.history.back();
        }
    }, [canView]);

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        // Several list endpoints return 404 (not 200 + []) when their
        // result set is empty — recover so the page still loads.
        // Affected: /tenantdeliverypartner/getdeliverypartner,
        // /concessionairecharges/getcharges.
        const safe404 = (p) => p.catch((err) => {
            if (err?.response?.status === 404) return { data: [] };
            throw err;
        });
        Promise.all([
            axios.get(`${config.hostname}/tenant/${id}`, { headers }),
            safe404(axios.post(`${config.hostname}/tenantdeliverypartner/getdeliverypartner`, { tenant_id: id }, { headers })),
            safe404(axios.post(`${config.hostname}/concessionairecharges/getcharges`, { concessionaire_id: concessionaireId }, { headers })),
        ])
            .then(([tenantRes, partnersRes, chargesRes]) => {
                if (cancelled) return;
                setTenant(tenantRes.data || null);
                setDeliveryPartners(Array.isArray(partnersRes.data) ? partnersRes.data : []);
                setCharges(
                    (Array.isArray(chargesRes.data) ? chargesRes.data : []).map((item) => ({
                        other_charge_id: item.other_charge_id?._id,
                        display_name: item.display_name,
                        charge_name: item.other_charge_id?.charge_name,
                        slot_index: item.slot_index,
                    })),
                );
            })
            .catch((err) => {
                console.error('tenant detail load failed', err);
            });
        return () => { cancelled = true; };
    }, [id, headers, concessionaireId, refreshKey]);

    if (!canView) return null;
    if (!tenant) return (
        <div className="v3-sadmin">
            <div className="v3-sadmin-empty"><p>{t('Loading tenant…')}</p></div>
        </div>
    );

    const deliveryNames = (deliveryPartners || [])
        .map((p) => p.delivery_partner_id?.delivery_partner_name)
        .filter(Boolean)
        .join(', ');

    // Bind by slot_index, not by the catalog's charge_name. Those catalog rows
    // are named "charge1".."charge5" and read as authoritative, but the stored
    // amounts follow the assignment's slot, which for several concessionaires
    // is a different number entirely.
    const chargeValueFor = (slotIndex) => {
        const map = {
            1: tenant.tenant_charge1,
            2: tenant.tenant_charge2,
            3: tenant.tenant_charge3,
            4: tenant.tenant_charge4,
            5: tenant.tenant_charge5,
        };
        return map[slotIndex];
    };

    return (
        <Fragment>
            <div className="v3-sadmin">
                <div className="v3-sadmin-hero">
                    <div className="v3-sadmin-hero-text">
                        <p className="v3-label" style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <ChevronLeft size={12} />
                            <span
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate(`${process.env.PUBLIC_URL}/admin/tenant/viewtenant/default`)}
                            >
                                {t('Tenants')}
                            </span>
                        </p>
                        <h1 className="v3-display v3-sadmin-greeting">
                            <span className="v3-sadmin-name">{tenant.tenant_store_name || t('Tenant')}</span>
                        </h1>
                        <p className="v3-body v3-sadmin-sub">
                            {tenant.tenant_brand_name} · {tenant.tenant_property_id?.property_name} · {t('Tenant code')} {tenant.tenant_code}
                        </p>
                    </div>
                    <div className="v3-sadmin-hero-actions">
                        {canManage && (
                            <button
                                type="button"
                                className="v3-btn v3-btn-ghost"
                                onClick={() => setStatusModalOpen(true)}
                            >
                                {t('Change status')}
                            </button>
                        )}
                        {/* "Edit lease" only useful when the user can also reach the
                            Edit Tenant page (TenantEdit requires manageTenants or
                            manageGeneralSettings to load). Show only if both
                            permissions hold. */}
                        {canEditLease && canManage && (
                            <button
                                type="button"
                                className="v3-btn v3-btn-ghost"
                                onClick={() => navigate(`${process.env.PUBLIC_URL}/admin/tenant/tenantedit/${id}#tenancy`)}
                            >
                                {t('Edit lease')}
                            </button>
                        )}
                        {canManage && (
                            <button
                                type="button"
                                className="v3-btn v3-btn-primary"
                                onClick={() => navigate(`${process.env.PUBLIC_URL}/admin/tenant/tenantedit/${id}`)}
                            >
                                <Edit2 size={14} /> {t('Edit tenant')}
                            </button>
                        )}
                    </div>
                </div>

                <TenantProfileStrip tenant={tenant} onChangeStatus={canManage ? () => setStatusModalOpen(true) : undefined} />

                {/* Tabs */}
                <div className="v3-card v3-sadmin-list-card">
                    <div className="v3-settings-toolbar">
                        <div className="v3-settings-tabs" role="tablist">
                            {TABS.map((tb) => (
                                <button
                                    key={tb.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={tb.id === tab}
                                    className={`v3-settings-tab ${tb.id === tab ? 'active' : ''}`}
                                    onClick={() => setTab(tb.id)}
                                >
                                    {t(tb.label)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div style={{ padding: '22px 24px' }}>
                        {tab === 'overview' && (
                            <div className="v3-sadmin-kv">
                                <Kv label={t('Brand')} value={tenant.tenant_brand_name} />
                                <Kv label={t('Concessionaire')} value={tenant.tenant_concessionaire_id?.concessionaire_name} />
                                <Kv label={t('Concessionaire code')} value={tenant.tenant_concessionaire_id?.concessionaire_code} />
                                <Kv label={t('Country')} value={tenant.tenant_country_id?.country_name} />
                                <Kv label={t('Property')} value={tenant.tenant_property_id?.property_name} />
                                <Kv label={t('Mall code')} value={tenant.tenant_property_id?.property_mall_code} />
                                <Kv label={t('Category')} value={tenant.tenant_category_id?.category_name} />
                                <Kv label={t('Subcategory')} value={tenant.tenant_subcategory_id?.subcategory_name} />
                                <Kv label={t('Group')} value={tenant.tenant_group_name} />
                                <Kv label={t('Franchisee')} value={tenant.tenant_franchisee ? t('Yes') : t('No')} />
                                <Kv label={t('Email')} value={tenant.tenant_email_id} />
                                <Kv label={t('Address')} value={tenant.tenant_address} full />
                                <Kv label={t('Tenant code')} value={tenant.tenant_code} />
                                <Kv label={t('Unit code')} value={tenant.tenant_unit_code} />
                                <Kv label={t('Store type')} value={tenant.tenant_store_type} />
                                <Kv label={t('Current status')} value={tenant.tenant_current_status} />
                                <Kv label={t('Carpet area')} value={tenant.tenant_carpet_area} />
                                <Kv label={t('Chargeable area')} value={tenant.tenant_chargable_area} />
                                <Kv label={t('Floor')} value={tenant.tenant_floor} />
                                <Kv label={t('Location')} value={tenant.tenant_location} />
                                <Kv label={t('Contact person')} value={tenant.tenant_contact_person} />
                                <Kv label={t('Contact number')} value={tenant.tenant_contact_number} />
                                <Kv label={t('IT contact')} value={tenant.tenant_it_contact_person} />
                                <Kv label={t('IT contact number')} value={tenant.tenant_it_contact_number} />
                                <Kv label={t('POS name')} value={tenant.tenant_pos_name} />
                                <Kv label={t('Data capture method')} value={tenant.tenant_data_capture_method} />
                                <Kv label={t('Data transfer method')} value={tenant.tenant_mode_of_transfer} />
                            </div>
                        )}

                        {tab === 'tenancy' && (
                            <div className="v3-sadmin-kv">
                                <Kv label={t('Lease start')} value={fmtDate(tenant.tenant_lease_startdate)} />
                                <Kv label={t('Lease end')} value={fmtDate(tenant.tenant_lease_enddate)} />
                                <Kv label={t('Base rent')} value={tenant.tenant_base_rent} />
                                <Kv label={t('Revenue share %')} value={tenant.tenant_revenue_share} />
                                <Kv
                                    label={t('Revenue share method')}
                                    value={tenant.tenant_revenue_share_method_id?.description}
                                    full
                                />
                                {charges.map((c, i) => (
                                    <Kv
                                        key={c.other_charge_id}
                                        label={c.display_name}
                                        value={chargeValueFor(c.slot_index || i + 1)}
                                    />
                                ))}
                                {deliveryNames && (
                                    <Kv label={t('Delivery partners')} value={deliveryNames} full />
                                )}
                            </div>
                        )}

                        {tab === 'activity' && (
                            <div className="v3-sadmin-kv">
                                <Kv label={t('Created date')} value={fmtDate(tenant.tenant_created_date)} />
                                <Kv label={t('Created by')} value={tenant.tenant_created_by?.userFirstName} />
                                <Kv label={t('Modified date')} value={fmtDate(tenant.tenant_modified_date)} />
                                <Kv label={t('Disabled')} value={tenant.tenant_disable ? t('Yes') : t('No')} />
                                <Kv label={t('UAT date')} value={fmtDate(tenant.tenant_uat_date)} />
                                <Kv label={t('UAT assignee')} value={tenant.tenant_uat_assignee} />
                                <Kv label={t('Live date')} value={fmtDate(tenant.tenant_live_date)} />
                                <Kv label={t('Live assignee')} value={tenant.tenant_live_assignee} />
                                <Kv label={t('Disable date')} value={fmtDate(tenant.tenant_disable_date)} />
                                <Kv label={t('Disable assignee')} value={tenant.tenant_disable_assignee} />
                                <Kv label={t('Disable reason')} value={tenant.tenant_disable_reason} full />
                                <Kv label={t('Reactivate date')} value={fmtDate(tenant.tenant_reactivate_date)} />
                                <Kv label={t('Reactivate assignee')} value={tenant.tenant_reactivate_assignee} />
                                <Kv label={t('Reactivate reason')} value={tenant.tenant_reactivate_reason} full />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {statusModalOpen && (
                <TenantStatusModal
                    tenantId={id}
                    onClose={() => setStatusModalOpen(false)}
                    onSaved={() => setRefreshKey((k) => k + 1)}
                />
            )}
        </Fragment>
    );
};

export default TenantDtl;
